// TechStackSection.jsx
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function TechStackSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const itemsRef = useRef([]);
  const mobileRefs = useRef([]);

  const tech = [
    { name: "MongoDB", img: "https://cdn.worldvectorlogo.com/logos/mongodb-icon-1.svg" },
    { name: "Express", img: "https://cdn.worldvectorlogo.com/logos/express-109.svg" },
    { name: "React", img: "https://cdn.worldvectorlogo.com/logos/react-2.svg" },
    { name: "Node.js", img: "https://cdn.worldvectorlogo.com/logos/nodejs-icon.svg" },
    { name: "PostgreSQL", img: "https://cdn.worldvectorlogo.com/logos/postgresql.svg" },
    { name: "Firebase", img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg" },
    { name: "AWS", img: "https://cdn.worldvectorlogo.com/logos/aws-2.svg" },
    { name: "Next.js", img: "https://cdn.worldvectorlogo.com/logos/nextjs-2.svg" },
    { name: "Python", img: "https://cdn.worldvectorlogo.com/logos/python-5.svg" },
    { name: "GitHub", img: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg" },
    { name: "TailwindCSS", img: "https://www.vectorlogo.zone/logos/tailwindcss/tailwindcss-icon.svg" },
    { name: "Java", img: "https://cdn.worldvectorlogo.com/logos/java-4.svg" },
  ];

  /* DESKTOP ANIMATION */
  useEffect(() => {
    if (window.innerWidth < 768) return;

    gsap.fromTo(
      itemsRef.current,
      { x: "-120vw", opacity: 0, scale: 0.4 },
      {
        x: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 55%", // trigger at just over half height
        },
      }
    );
  }, []);

  /* MOBILE ANIMATION */
  useEffect(() => {
    if (window.innerWidth >= 768) return;

    gsap.fromTo(
      mobileRefs.current,
      { opacity: 0, y: 180, scale: 0.85 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.65,
        stagger: 0.09,
        ease: "back.out(1.6)",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 66%", // trigger at 2/3 height
        },
      }
    );
  }, []);

  return (
    <section ref={sectionRef} className="py-14 px-4 relative">

      <style>{`
        /* Shared icon size */
        .tech-ball img, .mobile-ball img {
          width: 52px;
          height: 52px;
        }

        /* ========================================
           DESKTOP STYLING
        ======================================== */
        @media (min-width: 768px) {
          .tech-cluster {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: -40px;
          }

          .tech-ball {
            width: 110px;
            height: 110px;
            border-radius: 50%;
            background: white;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid rgba(120,95,20,0.15);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            margin: -22px;
            position: relative;

            /* smoother hover transition */
            transition: transform 0.35s ease, box-shadow 0.35s ease;
          }

          .tech-ball:hover {
            transform: translateY(-14px) scale(1.20);
            box-shadow: 0 10px 34px rgba(120,95,20,0.45);
            z-index: 50;
          }

          .tooltip {
            position: absolute;
            top: -52px;
            left: 50%;
            transform: translateX(-50%);
            background: white;
            padding: 6px 10px;
            border-radius: 8px;
            border: 1px solid rgba(120,95,20,0.25);
            box-shadow: 0 4px 12px rgba(0,0,0,0.20);
            opacity: 0;
            font-size: 0.75rem;
            transition: 0.25s ease;
            white-space: nowrap;
          }

          .tech-ball:hover .tooltip {
            top: -66px;
            opacity: 1;
          }

          .tech-grid {
            display: none !important;
          }
        }

        /* ========================================
           MOBILE STYLING
        ======================================== */
        @media (max-width: 767px) {
          .tech-cluster {
            display: none !important;
          }

          .tech-grid {
            display: grid !important;
            grid-template-columns: repeat(3, 1fr);
            gap: 18px;
            padding-top: 16px;
          }

          .mobile-ball {
            background: white;
            border-radius: 16px;
            padding: 14px;
            border: 1px solid rgba(120,95,20,0.15);
            box-shadow: 0 2px 12px rgba(0,0,0,0.12);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: 0.25s;
          }

          .mobile-ball:active {
            transform: scale(0.93);
          }
        }
      `}</style>

      <div className="max-w-5xl mx-auto text-center">

        {/* TRIGGER ELEMENT */}
        <h2
          ref={headingRef}
          className="text-xl sm:text-2xl font-copperplate text-[#785f14]"
        >
          A Few Technologies We Build With
        </h2>

        <p className="text-gray-600 max-w-xl mx-auto mt-2">
          A modern, battle-tested stack used to build fast, scalable, and reliable products.
        </p>

        {/* DESKTOP TECH BALLS */}
        <div className="tech-cluster mt-10">
          {tech.map((t, i) => (
            <div
              key={i}
              ref={el => itemsRef.current[i] = el}
              className="tech-ball"
            >
              <img src={t.img} alt={t.name} />
              <div className="tooltip">{t.name}</div>
            </div>
          ))}
        </div>

        {/* MOBILE TECH GRID */}
        <div className="tech-grid mt-10">
          {tech.map((t, i) => (
            <div
              key={i}
              ref={el => mobileRefs.current[i] = el}
              className="mobile-ball"
            >
              <img src={t.img} alt={t.name} />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
